import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { UsersIcon } from "lucide-react";

import { InvitationOfType, UseInvitationOptions } from "simplydo/interfaces";

import {
  acceptAuthenticatedInvitation,
  rejectAuthenticatedInvitation,
  removeAuthenticatedInvitation,
  toastError,
  toastMessage,
  toastSuccess,
  withInvitationPrompt,
} from "./internal";
import type { InvitationViewModel } from "./types";
import { emptyInvitationViewModel } from "./types";

export const useRequestJoinIdeaInvitation = (
  invitation: InvitationOfType<"requestJoinIdea"> | undefined,
  options?: UseInvitationOptions,
): InvitationViewModel => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const idea = invitation?.idea;
  const ideaId = idea?._id;
  const ideaName = idea?.name || t("invitations.deletedIdea");
  const inviterName = invitation?.inviterUser?.profile?.fullName;
  const canAccept = !!invitation?.canAccept && !!idea;
  const createdByCurrentUser = !!invitation?.createdByCurrentUser;

  const acceptInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.requestJoinIdea.acceptTitle"),
      t("invitations.requestJoinIdea.acceptMessage", {
        inviterName,
        name: idea?.name,
        idea: t("generic.idea"),
      }),
      () => {
        acceptAuthenticatedInvitation(
          invitation,
          () => {
            toastSuccess(t("invitations.requestAccepted"));
            navigate(`/ideas/${ideaId}`);
          },
          () => toastError(t("invitations.requestAcceptError")),
        );
      },
    );
  }, [idea?.name, ideaId, invitation, inviterName, navigate, options, t]);

  const rejectInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.requestJoinIdea.rejectTitle"),
      t("invitations.requestJoinIdea.rejectMessage", {
        inviterName,
        name: ideaName,
        idea: t("generic.idea"),
      }),
      () => {
        rejectAuthenticatedInvitation(
          invitation,
          () => toastMessage(t("invitations.requestRejected")),
          () => toastError(t("invitations.requestRejectError")),
        );
      },
    );
  }, [ideaName, invitation, inviterName, options, t]);

  const removeInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.requestJoinIdea.removeTitle"),
      t("invitations.requestJoinIdea.removeMessage", {
        name: ideaName,
        idea: t("generic.idea"),
      }),
      () => {
        removeAuthenticatedInvitation(
          invitation,
          () => toastMessage(t("invitations.removed")),
          () => toastError(t("invitations.removeError")),
        );
      },
    );
  }, [ideaName, invitation, options, t]);

  return useMemo(() => {
    if (!invitation) {
      return emptyInvitationViewModel();
    }

    return {
      icon: UsersIcon,
      link: ideaId ? `/ideas/${ideaId}` : null,
      isInvalid: !idea,
      text: {
        title: ideaName,
        fullTitle: t("invitations.requestJoinIdea.fullPageTitle", {
          name: ideaName,
          idea: t("generic.idea"),
        }),
        youInvited: t("invitations.requestJoinIdea.creatorPostText", {
          idea: t("generic.idea"),
        }),
        hasInvitedYou: t("invitations.requestJoinIdea.receivedPostText", {
          idea: t("generic.idea"),
        }),
        dismiss: createdByCurrentUser
          ? t("invitations.remove")
          : t("invitations.reject"),
        invalid: t("invitations.requestJoinIdea.invalidMessage", {
          idea: t("generic.idea"),
        }),
        ...(ideaId
          ? { goTo: t("invitations.idea.view", { idea: t("generic.idea") }) }
          : {}),
      },
      acceptInvite: canAccept ? acceptInvite : undefined,
      rejectInvite: !createdByCurrentUser ? rejectInvite : undefined,
      removeInvite: createdByCurrentUser ? removeInvite : undefined,
    };
  }, [
    acceptInvite,
    canAccept,
    createdByCurrentUser,
    idea,
    ideaId,
    ideaName,
    invitation,
    removeInvite,
    rejectInvite,
    t,
  ]);
};
